<template>
    <div class="relative">
        <div v-if="loading" class="absolute left-0 top-0 right-0 bottom-0 text-center font-extrabold flex flex-col justify-center text-xl">
            Loading...
        </div>
        <div class="mb-4 w-full flex justify-between" :class="{ 'opacity-10': loading }">
            <div>
                Records:
                <select
                    v-model.number="userPageSize"
                    class="inline-block w-18 ml-1 px-3 py-0"
                >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="0">All</option>
                </select>
            </div>
            <div>
                <button
                    v-if="!loading"
                    type="button"
                    class="rounded-lg bg-gray-600 hover:bg-gray-500 text-white px-3 py-2 focus:outline-none"
                    @click="() => getData()"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 inline-block align-middle">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    <span class="align-middle ml-2">
                        Reload
                    </span>
                </button>
            </div>
            <div v-if="filterable"
                 class="flex justify-end cursor-pointer select-none hover:opacity-80"
                 @click="toggleFilterDisplay"
            >
                <div class="pt-1">
                    {{ displayFilters ? 'Hide' : 'Show' }} Filters
                </div>
                <svg fill="none"
                     stroke="currentColor"
                     viewBox="0 0 24 24"
                     xmlns="http://www.w3.org/2000/svg"
                     class="w-6 h-6 ml-2 outline-none"
                >
                    <path stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    >
                    </path>
                </svg>
            </div>
        </div>

        <div class="border bg-white rounded-lg shadow p-0 relative overflow-auto" :class="{ 'opacity-10': loading }">
            <table class="w-full">
                <thead>
                    <tr class="border-black" :class="{'border-b-2': !displayFilters}">
                        <th v-if="editButton" class="w-14"></th>
                        <th
                            v-if="debugStore.debugMode"
                            class="capitalize text-left p-2 hover:bg-gray-100 whitespace-nowrap"
                            :class="{'cursor-pointer': sortable }"
                            @click="() => {
                                if (sortable) {
                                    handleSort('id');
                                }
                            }"
                        >
                            ID
                            <span v-if="sort.field === 'id' && sort.asc">&#9650;</span>
                            <span v-if="sort.field === 'id' && !sort.asc">&#9660;</span>
                        </th>
                        <th
                            v-for="col in mappedColumns"
                            class="capitalize text-left p-2 hover:bg-gray-100 whitespace-nowrap"
                            :class="{'cursor-pointer': sortable }"
                            @click="() => {
                                if (sortable) {
                                    handleSort(col.field);
                                }
                            }"
                        >
                            {{ col.heading }}
                            <span v-if="sort.field === col.field && sort.asc">&#9650;</span>
                            <span v-if="sort.field === col.field && !sort.asc">&#9660;</span>
                        </th>
                    </tr>
                    <tr v-if="displayFilters" class="border-black border-b-2">
                        <th v-if="editButton"></th>
                        <th v-if="debugStore.debugMode">
                            <input-text type="number" @input="(value) => handleFilter('id', value)" />
                        </th>
                        <th
                            v-for="col in mappedColumns"
                            class="text-left p-2"
                        >
                            <input-text v-if="col.filter.type === 'text'" @input="(value) => handleFilter(col.field, value)" />
                            <input-text v-if="col.filter.type === 'number'" type="number" @input="(value) => handleFilter(col.field, value)" />

                            <Select
                                :value="filters[col.field]"
                                v-if="col.filter.type === 'select'"
                                :options="[
                                    { value: '', name: '' },
                                    ...col.filter.options,
                                ]"
                                @input="(value) => handleFilter(col.field, value)"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        class="even:bg-gray-50 odd:bg-gray-300 hover:bg-blue-300"
                        v-for="row in processedRows"
                    >
                        <td v-if="editButton" class="p-2">
                            <a
                                class="block"
                                :href="row.edit_route"
                                target="_blank"
                            >
                                <svg
                                    class="h-5 w-5 m-auto"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                </svg>
                            </a>
                        </td>
                        <td v-if="debugStore.debugMode">{{ row.id }}</td>
                        <td v-for="col in mappedColumns" class="p-2 whitespace-nowrap">
                            <span v-if="!col.template">
                                {{ getCellData(col.field, row) }}
                            </span>
                            <span v-if="col.template" v-html="getCellData(col.field, row)"></span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="!filteredRows.length" class="w-full text-center font-bold mt-4" :class="{ 'opacity-10': loading }">
            No items
        </div>
        <div v-if="filteredRows.length" class="flex w-full mt-4" :class="{ 'opacity-10': loading }">
            <div class="w-1/2">
                {{ (page - 1) * userPageSize + 1 }}
                -
                {{
                    Math.min(
                        page *
                        ( userPageSize || filteredRows.length ), // if page size is 'All', userPageSize is 0, this needs to be the total row count
                        filteredRows.length
                    )
                }}
                of
                {{ filteredRows.length }}
                items
            </div>
            <div class="w-1/2 flex justify-end">
                <ul class="inline-flex bg-white cursor-pointer select-none">
                    <li @click="page = 1" class="p-3 text-gray-400 hover:text-gray-700"><<</li>
                    <li @click="page = Math.max(1, page - 1)" class="p-3 text-gray-400 hover:text-gray-700"><</li>
                    <li v-for="p in paginationPageOptions"
                        @click="page = p"
                        class="p-3 hover:text-gray-700"
                        :class="{
                            'text-black font-bold': p === page,
                            'text-gray-400': p !== page,
                        }"
                    >
                        {{ p }}
                    </li>
                    <li @click="page = Math.min(pages, page + 1)" class="p-3 text-gray-400 hover:text-gray-700">></li>
                    <li @click="page = pages" class="p-3 text-gray-400 hover:text-gray-700">>></li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import InputText from "../form/InputText";
import reference from "../../mixins/reference";
import {v4 as uuidv4} from "uuid";
import siteLanguage from "../../config/siteLanguage";
import {useDebugStore} from "../../stores/debug";

export default {
    name: "RocketGrid",
    components: {InputText},
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        value: {
            type: Object,
            required: false,
        },
        method: {
            type: String,
            required: false,
            default: 'get',
        },
        editButton: {
            type: Boolean,
            required: false,
            default: true,
        },
        sortable: {
            type: Boolean,
            required: false,
            default: true,
        },
        filterable: {
            type: Boolean,
            required: false,
            default: true,
        },
        routes: {
            /**
             * {
             *     get: 'api.casinoReviews.index',
             *     delete: '',
             *     update: '',
             * }
             */
            type: Object,
            required: false,
        },
        with: {
            /**
             * Array of strings: ['casino', 'author']
             */
            type: Array,
            required: false,
            default: () => [],
        },
        language: {
            type: Object,
            required: false,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
        columns: {
            /**
             * Can be:
             * a simple array of strings: ['name', 'slug', 'casino.name', 'author.full_name'], the headings will be derived from the field names
             * or
             * an array of objects: [
             *     { field: 'casino.name', heading: 'Casino Name', filter: {type: 'text'} }, // filter type can be text, number, select
             *     { field: 'author.full_name', heading: 'Author', filter: {type: 'select', options: [{value: 'a', name: 'b'}]} },
             *     { field: 'name' } // heading is optional
             *     { field: 'affiliate_url', template: (row) => { return `<a href='${row.affiliate_url}' target='_blank'>${row.affiliate_url}</a>`; }}
             * ]
             *
             * Note: when using template, v-html is used, be careful with XSS
             */
            type: Array,
            required: true,
        },
        pageSize: {
            /**
             * 0 = unlimited
             */
            type: Number,
            required: false,
            default: 25,
            validator: function (value) {
                return value >= 0;
            }
        },
    },
    methods: {
        toggleFilterDisplay() {
            this.displayFilters = !this.displayFilters;

            if (!this.displayFilters) {
                this.filters = {};
            }
        },
        handleFilter(field, value) {
            // this.filters[field] = value;
            this.$set(this.filters, field, value);
        },
        handleSort(field) {
            if (this.sort.field === field) {
                if (this.sort.asc) {
                    this.sort.asc = !this.sort.asc;
                } else {
                    // reset
                    this.sort.field = null;
                    this.sort.asc = true;
                }
            }
            else {
                this.sort.field = field;
                this.sort.asc = true;
            }
        },
        getCellData(field, row) {
            let template = this.mappedColumns.filter(col => col.field === field)[0]?.template;
            if (template && typeof template === 'function') {
                return template(row);
            }

            if (field.includes('.')) {
                let data = row;
                for (const e of field.split('.')) {
                    data = data[e];
                    if (data == null) {
                        return null;
                    }
                }
                return data;
            }
            return row[field];
        },
        async getData() {
            if (this.routes?.get && this.columns.length) {
                let url = route(this.routes.get, {
                    with: this.with,
                    language: this.language.id,
                });

                this.loading = true;

                const {data} = await axios({
                    method: this.method,
                    url: url,
                }).catch((err) => {
                    console.error(err);
                    const errorMessage = this.constructErrorMessage(err);

                    this.$notify({
                        group: "formError",
                        title: "Error Previewing Site",
                        text: errorMessage,
                    });
                }).finally(() => {
                    this.loading = false;
                });

                this.rows = data.data;
                this.page = 1;
            }
        },
        getHeadingFromFieldName(field) {
            return field.replaceAll('.', ' ').replaceAll('_', ' ');
        },
    },
    mounted() {
        this.getData();
    },
    computed: {
        mappedColumns() {
            if (this.columns.length && typeof this.columns[0] === 'string') {
                return this.columns.map(field => {
                    return {
                        field: field,
                        heading: this.getHeadingFromFieldName(field),
                        filter: { type: 'text' },
                        template: null,
                    }
                });
            }
            return this.columns.map(col => {
                return {
                    field: col.field,
                    heading: col.heading ?? this.getHeadingFromFieldName(col.field),
                    filter: col.filter ?? { type: 'text'},
                    template: col.template ?? null,
                };
            });
        },
        paginationPageOptions() {
            // max options has to be an odd number
            const MAX_OPTIONS = 5;
            let half = Math.floor(MAX_OPTIONS / 2);

            if (this.pages <= MAX_OPTIONS) {
                return [...Array(this.pages).keys()].map(e => e + 1);
            }

            let options = [...Array(MAX_OPTIONS).keys()];

            if (this.page - half < 1) {
                return options.map(e => e + 1);
            }

            if (this.page + half > this.pages) {
                return options.map(e => this.pages - e).reverse();
            }

            return options.map(e => e + this.page - half);
        },
        pages() {
            if (this.userPageSize === 0) {
                return 1;
            }

            return Math.ceil(this.filteredRows.length / this.userPageSize);
        },
        filteredRows() {
            let rows = [...this.rows];

            // handle filtering
            for (let [field, value] of Object.entries(this.filters)) {
                value = value.toString().trim();
                if (value === '') {
                    continue;
                }

                rows = rows.filter(row => {
                    let cellData = this.getCellData(field, row);
                    if (cellData == null) {
                        return false;
                    }

                    return cellData.toString().toUpperCase()
                        .includes(value.toUpperCase());
                });
            }

            return rows;
        },
        processedRows() {
            // filtered, sorted, paged rows

            let rows = [...this.filteredRows];

            if (this.sort.field) {
                // handle sorting
                // data is sorted alphabetically, case-insensitive
                // if the string contains a number, then it's treated as a number
                rows = rows.sort((a, b) => {
                    let direction = this.sort.asc ? 1 : -1;
                    a = this.getCellData(this.sort.field, a)?.toString().toUpperCase();
                    b = this.getCellData(this.sort.field, b)?.toString().toUpperCase();

                    // if the strings are numbers, then parse them as numbers
                    if (!isNaN(a) && !isNaN(b)) {
                        a = parseFloat(a);
                        b = parseFloat(b);
                    }

                    if (a < b) return -direction;
                    if (a > b) return direction;
                    return 0;
                });
            }

            if (this.userPageSize > 0) {
                // pagination

                let startIndex = (this.page - 1) * this.userPageSize;
                let endIndex = startIndex + this.userPageSize;
                rows = rows.slice(startIndex, endIndex);
            }
            return rows;
        },
    },
    data(){
        return {
            rows: this.value ?? [],
            loading: false,
            page: 1,
            userPageSize: this.pageSize,
            sort: {
                field: null,
                asc: true,
            },
            displayFilters: false,
            filters: {},
            debugStore: useDebugStore(),
        }
    },
    watch: {
        userPageSize: function (newValue) {
            // if page size is changed, reset to first page
            this.page = 1;
        },
        pages: function (newValue) {
            // if number of pages changes (e.g when filtering), reset to first page
            this.page = 1;
        },
    },
}
</script>
